import { useState } from 'react';
import CacSetup from './CacSetup';
import { MfaSetupFullPage } from './MfaSetupFullPage';
import { COLORS, FONTS, GRADIENTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import cacIconUrl from '../assets/cac-icon.svg';
import faceIdIconUrl from '../assets/face-id-icon.svg';
import phoneMfaIconUrl from '../assets/phone-mfa-icon.svg';

type SetupMethod = 'cac' | 'mfa' | 'biometric';

/** Keycloak actions for each auth method */
const METHOD_ACTION: Record<string, string> = {
  mfa: 'CONFIGURE_TOTP',
  biometric: 'webauthn-register',
};

/** Display labels for each method */
const METHOD_LABEL: Record<string, string> = {
  mfa: 'Phone MFA',
  biometric: 'Biometric / Passkey',
};

/** Auth method definitions for the method picker */
const AUTH_METHODS: { id: SetupMethod; label: string; icon: string }[] = [
  { id: 'cac', label: 'CAC', icon: cacIconUrl },
  { id: 'biometric', label: 'BIOMETRIC', icon: faceIdIconUrl },
  { id: 'mfa', label: 'MULTI-FACTOR', icon: phoneMfaIconUrl },
];

interface AuthSetupOverlayProps {
  method: SetupMethod;
  username: string;
  onComplete: () => void;
  onSkip: () => void;
  onChangeMethod: (method: SetupMethod) => void;
}

/**
 * Full-page auth setup screen shown after registration.
 * Completely replaces the Hub until the user completes or skips setup.
 * Includes a "Change Method" option so users can pick a different auth method.
 */
export function AuthSetupOverlay({ method, username, onComplete, onSkip, onChangeMethod }: AuthSetupOverlayProps): React.JSX.Element {
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = prefersDark;
  const colors: ColorTheme = isDark ? COLORS.dark : COLORS.light;

  function handleSelectMethod(m: SetupMethod): void {
    setShowMethodPicker(false);
    onChangeMethod(m);
  }

  /** Method picker sub-screen */
  if (showMethodPicker) {
    return (
      <FullPageWrapper isDark={isDark} colors={colors}>
        <h2 style={{
          fontFamily: FONTS.heading, fontSize: 14, letterSpacing: '0.15em',
          color: colors.textSecondary, textTransform: 'uppercase', margin: 0,
        }}>
          Choose an Authentication Method
        </h2>
        <p style={{ fontSize: 13, color: colors.textSecondary, textAlign: 'center', margin: 0 }}>
          Select your preferred method for securing your account.
        </p>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          {AUTH_METHODS.map(m => (
            <button
              key={m.id}
              onClick={() => handleSelectMethod(m.id)}
              style={{
                width: 120, padding: '20px 12px', borderRadius: 12, cursor: 'pointer',
                border: m.id === method ? '2px solid #4ADE80' : `1px solid ${colors.border}`,
                backgroundColor: m.id === method
                  ? (isDark ? 'rgba(74, 222, 128, 0.08)' : 'rgba(74, 222, 128, 0.05)')
                  : colors.card,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              }}
            >
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                backgroundColor: isDark ? '#292929' : '#EEEEEE',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src={m.icon} alt={m.label} style={{ width: 28, height: 28, filter: isDark ? 'none' : 'invert(1)' }} />
              </div>
              <span style={{
                fontFamily: FONTS.heading, fontSize: 10, letterSpacing: '0.1em',
                color: colors.text, textTransform: 'uppercase',
              }}>
                {m.label}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowMethodPicker(false)}
          style={{
            marginTop: 8, background: 'none', border: 'none',
            color: colors.textSecondary, fontFamily: FONTS.body, fontSize: 12,
            cursor: 'pointer', textDecoration: 'underline', padding: 4,
          }}
        >
          Cancel
        </button>
      </FullPageWrapper>
    );
  }

  /** CAC setup */
  if (method === 'cac') {
    return (
      <FullPageWrapper isDark={isDark} colors={colors}>
        <CacSetup
          username={username}
          onComplete={onComplete}
          onSkip={onSkip}
          isDark={isDark}
        />
        <ChangeMethodLink onClick={() => setShowMethodPicker(true)} />
      </FullPageWrapper>
    );
  }

  /** MFA / Biometric setup */
  return (
    <FullPageWrapper isDark={isDark} colors={colors}>
      <MfaSetupFullPage
        action={METHOD_ACTION[method]}
        label={METHOD_LABEL[method]}
        onComplete={onComplete}
        onSkip={onSkip}
        isDark={isDark}
        colors={colors}
      />
      <ChangeMethodLink onClick={() => setShowMethodPicker(true)} />
    </FullPageWrapper>
  );
}

/* ─── Shared layout wrapper ─── */

interface FullPageWrapperProps {
  isDark: boolean;
  colors: ColorTheme;
  children: React.ReactNode;
}

function FullPageWrapper({ isDark, colors, children }: FullPageWrapperProps): React.JSX.Element {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      backgroundColor: isDark ? '#141416' : '#E8E8E8',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 16, fontFamily: FONTS.body,
    }}>
      {/* Top bar with heading */}
      <div style={{
        position: 'absolute', top: 32,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      }}>
        <h1 style={{
          fontFamily: FONTS.heading, fontSize: 20, letterSpacing: '0.2em',
          color: colors.text, margin: 0,
        }}>
          SETUP YOUR AUTHENTICATION
        </h1>
        <div style={{
          width: 200, height: 3, borderRadius: 2,
          background: GRADIENTS.lightbar,
        }} />
      </div>

      {children}
    </div>
  );
}

/* ─── Change method link ─── */

function ChangeMethodLink({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        marginTop: 4, background: 'none', border: 'none',
        color: COLORS.accent.blue, fontFamily: FONTS.heading, fontSize: 11,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        cursor: 'pointer', padding: '4px 8px',
      }}
    >
      Change Auth Method
    </button>
  );
}
